// CHANGE LOG — Full Login Flow with Org Branding
// - Variant A: Subdomain detected → M365 button + org branding (Parallel Minds/Data Smith AI)
// - Variant B: No subdomain → Normal email/password form +PM_People branding
// - ORG_CONFIG: parallel-minds | data-smith | default
// - Demo bypass: active on both variants
// - Existing subdomain detection + M365 flow: untouched
// - No post-login screen changes

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import { IdentityProviderResponse, User } from '@/types';
import { getSubdomain } from '@/utils/subdomain';
import { fetchIdentityProviderBySubdomain } from '@/utils/identityService';
import { buildAuthorizationUrl } from '@/config/authConfig';
import { STORAGE_KEYS, APP_NAME } from '@/config/appConfig';
import { ORG_CONFIG } from '@/config/orgBranding';
import { getOnboardingStatus, onboardingNeedsAction } from '@/features/onboarding';
import { sanitizeMessageParam } from '@/utils/urlSecurity';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import loginTheme from './theme/loginTheme';
import MuiButton from '@components/common/atoms/MuiButton';
import AppCard from '@components/common/organisms/AppCard';
import MuiCheckbox from '@components/common/atoms/MuiCheckbox';
import Badge from '@components/common/atoms/Badge';
import Input from '@components/common/molecules/Input';
import MuiAlert from '@components/common/molecules/MuiAlert';
import Icon from '@components/common/atoms/Icon';
import LoadingSpinner from '@components/common/atoms/LoadingSpinner';

const STAT_CARDS = [
  { icon: 'Building', label: 'Organisations', delay: '0s' },
  { icon: 'Clock', label: 'Attendance Tracking', delay: '0.1s' },
  { icon: 'Shield', label: 'M365 Integration', delay: '0.2s' },
  { icon: 'Users', label: 'Team Management', delay: '0.3s' },
];

function readStoredUser(): User | null {
  const raw = sessionStorage.getItem(STORAGE_KEYS.user);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

// Submitted/UnderReview/Completed all belong on the dashboard — only
// NotStarted/InProgress/RevisionRequested still need the wizard.
async function resolvePostLoginRoute(userData: User | null): Promise<string> {
  if (userData?.employeeId) {
    const status = await getOnboardingStatus(userData.employeeId).catch(() => null);
    if (!status || onboardingNeedsAction(status.status)) return '/onboarding';
  }
  return '/dashboard';
}

const LoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithPassword, loginWithDemoCredentials, error: authError, isAuthenticated } = useAuth();
  
  // Loading & Configuration States
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(true);
  
  // Subdomain & Org States
  const [subdomain, setSubdomain] = useState<string>('');
  const [orgConfig, setOrgConfig] = useState<IdentityProviderResponse | null>(null);
  const [domainStatus, setDomainStatus] = useState<'valid' | 'invalid' | null>(null);
  
  // Form States
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  
  // UI States
  const [showDemoBypass] = useState<boolean>(false);
  const [showDemoHint, setShowDemoHint] = useState<boolean>(false);

  const [successMessage, setSuccessMessage] = useState('');

  // Read error / success from URL query params (from OAuth callback or password-change redirect)
  useEffect(() => {
    // searchParams already decodes; decoding again would throw a URIError on a
    // value like "?error=%" and take the whole sign-in page down. The text is
    // rendered on our own domain, so it is sanitised and length-capped too.
    const errorParam = sanitizeMessageParam(searchParams.get('error'));
    if (errorParam) {
      setFormError(errorParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (searchParams.get('passwordChanged') === '1') {
      setSuccessMessage('Password changed successfully. Please sign in with your new password.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Org branding is a pure function of subdomain — no need to store it in state.
  // Plain lookup, not memoized: ORG_CONFIG is a static table (a single property
  // access, not expensive) and orgBranding is only read inline in this file's
  // own JSX, never passed to a child — no stable reference to protect.
  const orgBranding = ORG_CONFIG[subdomain as keyof typeof ORG_CONFIG] ?? ORG_CONFIG.default;

  // Redirect if already authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const redirect = async () => {
      const userData = readStoredUser();
      const route = await resolvePostLoginRoute(userData);
      if (!cancelled) navigate(route, { replace: true });
    };

    redirect();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Load subdomain and organization config
  useEffect(() => {
    let cancelled = false;

    const loadIdentityConfig = async () => {
      const extractedSubdomain = getSubdomain();
      if (!cancelled) setSubdomain(extractedSubdomain);

      // If subdomain exists, fetch M365 config
      if (extractedSubdomain) {
        try {
          const identity = await fetchIdentityProviderBySubdomain(extractedSubdomain);
          if (cancelled) return;
          setOrgConfig(identity);
          setDomainStatus('valid');

          if (identity.provider.toUpperCase() !== 'M365') {
            setDomainStatus('invalid');
            setFormError(`Unsupported login provider: ${identity.provider}`);
          }
        } catch (err: any) {
          if (!cancelled) {
            setDomainStatus('invalid');
            setFormError(err?.message || 'Unable to load organization configuration');
          }
        }
      }

      if (!cancelled) setLoadingConfig(false);
    };

    loadIdentityConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  // Display auth error from context
  useEffect(() => {
    if (authError) {
      setFormError(authError);
    }
  }, [authError]);

  // Form validation
  const validateEmail = (value: string): boolean => {
    setEmailError('');
    if (!value) {
      setEmailError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validatePassword = (value: string): boolean => {
    setPasswordError('');
    if (!value) {
      setPasswordError('Password is required');
      return false;
    }
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  // M365 Login Handler (Subdomain Mode) - Redirect to Azure AD
  const handleMicrosoftLogin = async () => {
    setFormError('');

    if (!orgConfig || orgConfig.provider.toUpperCase() !== 'M365') {
      setFormError('M365 configuration not available');
      return;
    }

    try {
      // Build authorization URL that redirects to backend callback
      const authUrl = buildAuthorizationUrl(orgConfig, subdomain);
      
      // Redirect user to Azure AD login
      window.location.href = authUrl;
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to initiate M365 login');
    }
  };

  // Email/Password Login Handler (Normal Mode)
  const handlePasswordLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    // Validate fields
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoading(true);

    try {
      // Check demo credentials (case-insensitive email, case-sensitive password)
      if (email.trim().toLowerCase() === 'system@pm-people.com' && password === 'Admin@123') {
        await loginWithDemoCredentials();
        // Clear probation notice flag to show modal after login
        sessionStorage.removeItem(STORAGE_KEYS.probationNoticeShown);
        // Demo user always has onboarding complete
        navigate('/dashboard', { replace: true });
      } else {
        // Real login (calls backend API)
        const loginResult = await loginWithPassword(email, password);

        // Temp password was used — go to change-password screen before anything else
        if (loginResult.requiresPasswordChange) {
          navigate('/change-password', { replace: true });
          return;
        }

        // Check onboarding status from user data and redirect accordingly
        const userData = readStoredUser();
        const route = await resolvePostLoginRoute(userData);
        // Clear probation notice flag to show modal after login (only if onboarding complete)
        if (route === '/dashboard' && userData?.isOnboardingComplete) {
          sessionStorage.removeItem(STORAGE_KEYS.probationNoticeShown);
        }
        navigate(route, { replace: true });
      }
    } catch (err: any) {
      const errorMessage = err?.message || 'Invalid email or password. Please try again.';
      // PASSWORD_NOT_SET is a special sentinel returned by the backend when the account
      // has no password yet (e.g. created via bulk import or admin without setting one).
      if (errorMessage === 'PASSWORD_NOT_SET') {
        // Redirect to set-password page with email pre-filled
        navigate(`/set-password?email=${encodeURIComponent(email)}`, { replace: true });
        return;
      }
      setFormError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Autofill demo credentials
  const handleAutofillDemo = () => {
    setEmail('system@pm-people.com');
    setPassword('Admin@123');
    setEmailError('');
    setPasswordError('');
    setFormError('');
  };

  // Loading screen
  if (loadingConfig) {
    return <LoadingSpinner message="Detecting organization settings" />;
  }

  const isSubdomainMode = !!subdomain;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={loginTheme}>
        <Box className="min-h-screen flex animate-in fade-in duration-300 overflow-x-hidden w-full">
          {/* LEFT PANEL - Branding */}
          <Box className={`hidden lg:flex lg:w-2/5 bg-gradient-to-br ${orgBranding.leftPanelBg} relative overflow-hidden flex-shrink-0`}>
            {/* Floating Geometric Shapes */}
            <Box className="absolute inset-0 overflow-hidden">
              <Box className="absolute top-20 left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl animate-float" />
              <Box className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
              <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
            </Box>

            <Box className="w-full h-full flex flex-col justify-between p-12 text-white relative z-10">
              {/* Logo and Brand */}
              <Box>
                <Box className="flex items-center space-x-4 mb-12 animate-in slide-in-from-left duration-500">
                  <Box className="relative group">
                    <Box className="absolute inset-0 bg-white/20 rounded-2xl blur-xl group-hover:bg-white/30 transition-all duration-300" />
                    <Box className="relative w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20 shadow-2xl">
                      <Icon name={orgBranding.logoIcon} size={32} className="text-white" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography component="h1" className="text-4xl font-bold tracking-tight">{orgBranding.logoText}</Typography>
                    <Typography component="p" className="text-sm text-white/70 font-medium mt-1">{orgBranding.name}</Typography>
                  </Box>
                </Box>

                <Box className="mb-8 animate-in slide-in-from-left duration-700 delay-100">
                  <Typography component="h2" className="text-3xl font-bold mb-4 leading-tight font-serif">
                    {orgBranding.tagline}
                  </Typography>
                  <Typography component="p" className="text-lg text-white/80 font-light leading-relaxed">
                    {isSubdomainMode
                      ? 'Your complete HR management solution integrated with Microsoft 365 for seamless collaboration and productivity.'
                      : 'Streamline operations, enhance productivity, and focus on what truly matters with our modern HR platform.'}
                  </Typography>
                </Box>

                {/* Floating Stat Cards (Default Mode Only) */}
                {!isSubdomainMode && (
                  <Box className="grid grid-cols-2 gap-4 mb-8 animate-in slide-in-from-left duration-700 delay-200">
                    {STAT_CARDS.map((stat) => (
                      <Box
                        key={stat.label}
                        className="p-4 bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 shadow-xl hover:bg-white/10 transition-all duration-300 animate-float"
                        style={{ animationDelay: stat.delay }}
                      >
                        <Icon name={stat.icon} size={24} className="text-white/80 mb-2" />
                        <Typography component="p" className="text-sm font-semibold text-white">{stat.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Testimonial Card */}
              <Box className="bg-white/10 backdrop-blur-2xl rounded-2xl p-6 border border-white/20 shadow-2xl animate-in slide-in-from-bottom duration-700 delay-300 hover:bg-white/15 transition-all">
                <Box className="flex items-start space-x-4">
                  <Box className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-lg font-bold shadow-lg flex-shrink-0">
                    HR
                  </Box>
                  <Box className="flex-1">
                    <Typography component="p" className="text-white/95 text-sm mb-3 leading-relaxed">
                      "{APP_NAME} keeps our team connected and productive every single day."
                    </Typography>
                    <Box>
                      <Typography component="p" className="text-white/60 text-xs">HR Lead · {orgBranding.name}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* RIGHT PANEL - Login Form */}
          <Box className="flex-1 flex items-center justify-center p-6 bg-white dark:bg-slate-900 w-full min-w-0 overflow-x-hidden">
            <Box className="w-full max-w-md px-4 sm:px-0">
              {/* Mobile Logo */}
              <Box className="lg:hidden flex items-center justify-center mb-8 animate-in fade-in duration-500">
                <Box className="flex items-center space-x-3">
                  <Box className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                    <Icon name={orgBranding.logoIcon} size={24} className="text-white" />
                  </Box>
                  <Box>
                    <Typography component="h1" className="text-2xl font-bold text-gray-900 dark:text-white">{orgBranding.logoText}</Typography>
                    <Typography component="p" className="text-xs text-gray-500 dark:text-gray-400">{orgBranding.name}</Typography>
                  </Box>
                </Box>
              </Box>

              <AppCard variant="mui" className="animate-in slide-in-from-right duration-500">
                {/* Header */}
                <Box className="mb-8">
                  <Typography component="h2" className="text-3xl font-bold text-gray-900 dark:text-white mb-2 font-serif">
                    {orgBranding.loginHeading}
                  </Typography>
                  <Typography component="p" className="text-gray-600 dark:text-gray-400">
                    {orgBranding.loginSubtext}
                  </Typography>
                  {isSubdomainMode && orgConfig?.domain && (
                    <Box className="mt-3 flex items-center justify-center space-x-2">
                      <Typography component="span" className="text-xs text-gray-500 dark:text-gray-400">Domain:</Typography>
                      <Typography component="span" className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">{orgConfig.domain}</Typography>
                      {domainStatus === 'valid' && <Badge engine="mui" variant="success">Verified</Badge>}
                      {domainStatus === 'invalid' && <Badge engine="mui" variant="error">Invalid</Badge>}
                    </Box>
                  )}
                </Box>

                {/* Success Message (e.g. after password change) */}
                {successMessage && (
                  <Box className="mb-4">
                    <MuiAlert severity="success">{successMessage}</MuiAlert>
                  </Box>
                )}

                {/* Error Message */}
                {formError && (
                  <Box className="mb-6">
                    <MuiAlert severity="error" onDismiss={() => setFormError('')}>{formError}</MuiAlert>
                  </Box>
                )}

                <Box className="border-t border-gray-200 dark:border-slate-800 my-6" />

                {/* VARIANT A - Subdomain Mode (M365) */}
                {isSubdomainMode ? (
                  <Box className="space-y-4">
                    {/* M365 Sign In Button */}
                    <MuiButton
                      variant="secondary"
                      size="lg"
                      fullWidth
                      onClick={handleMicrosoftLogin}
                      isLoading={loading && !showDemoBypass}
                      disabled={(loading && !showDemoBypass) || !orgConfig || orgConfig.provider.toUpperCase() !== 'M365'}
                      icon={
                        <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                          <rect x="12" y="1" width="10" height="10" fill="#7FBA00"/>
                          <rect x="1" y="12" width="10" height="10" fill="#00A4EF"/>
                          <rect x="12" y="12" width="10" height="10" fill="#FFB900"/>
                        </svg>
                      }
                    >
                      {(loading && !showDemoBypass) ? 'Redirecting to Microsoft...' : 'Sign in with Microsoft'}
                    </MuiButton>
                  </Box>
                ) : (
                  /* VARIANT B - Normal Mode (Email/Password) */
                  <Box className="space-y-6">
                    <Box component="form" noValidate onSubmit={handlePasswordLogin} className="space-y-4">
                      <Input
                        variant="mui"
                        id="login-email"
                        type="email"
                        label="Email Address"
                        icon={<Icon name="Mail" size={18} className="text-slate-400" />}
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEmail(e.target.value);
                          setEmailError('');
                          setFormError('');
                        }}
                        onBlur={() => validateEmail(email)}
                        error={emailError}
                        required
                      />

                      <Input
                        variant="mui"
                        id="login-password"
                        type="password"
                        label="Password"
                        icon={<Icon name="Lock" size={18} className="text-slate-400" />}
                        showPasswordToggle
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setPassword(e.target.value);
                          setPasswordError('');
                          setFormError('');
                        }}
                        onBlur={() => validatePassword(password)}
                        error={passwordError}
                        required
                      />

                      <Box className="flex items-center justify-between">
                        <MuiCheckbox
                          id="remember-me"
                          label="Remember me"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <MuiButton variant="ghost" onClick={() => navigate('/forgot-password')}>
                          Forgot password?
                        </MuiButton>
                      </Box>

                      <MuiButton type="submit" variant="primary" size="lg" fullWidth isLoading={loading} disabled={loading}>
                        {loading ? 'Signing In...' : 'Sign In'}
                      </MuiButton>
                    </Box>

                    {/* Demo Credentials Hint — development builds only. This panel prints a
                        working administrator login; import.meta.env.DEV is a compile-time
                        constant, so the block (and the credentials in it) is dropped from
                        production bundles entirely. */}
                    {import.meta.env.DEV && (
                    <Box className="border-t border-gray-200 dark:border-slate-800 pt-4">
                      <MuiButton
                        variant="ghost"
                        fullWidth
                        onClick={() => setShowDemoHint(!showDemoHint)}
                        sx={{ justifyContent: 'flex-start', color: 'text.secondary', fontWeight: 400 }}
                      >
                        <Icon name="Info" size={14} className="mr-1" />
                        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Demo access</Box>
                        <Icon
                          name="ChevronDown"
                          size={14}
                          className={`transition-transform ${showDemoHint ? 'rotate-180' : ''}`}
                        />
                      </MuiButton>

                      {showDemoHint && (
                        <Box className="mt-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl animate-in slide-in-from-top-2 duration-200">
                          <Typography component="p" className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-2">Demo Credentials:</Typography>
                          <Box className="space-y-1 text-sm text-amber-800 dark:text-amber-400">
                            <Typography component="p"><span className="font-medium">Email:</span> system@pm-people.com</Typography>
                            <Typography component="p"><span className="font-medium">Password:</span> Admin@123</Typography>
                          </Box>
                          <MuiButton
                            variant="secondary"
                            size="sm"
                            fullWidth
                            onClick={handleAutofillDemo}
                            icon={<Icon name="Wand2" size={14} />}
                            sx={{
                              mt: 1.5,
                              bgcolor: '#FEF3C7',
                              color: '#78350F',
                              borderColor: '#FCD34D',
                              '&:hover': { bgcolor: '#FDE68A', borderColor: '#FCD34D' },
                            }}
                          >
                            Autofill
                          </MuiButton>
                        </Box>
                      )}
                    </Box>
                    )}
                  </Box>
                )}

                {/* Footer */}
                <Box className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-800 text-center">
                  <Typography component="p" className="text-xs text-gray-500 dark:text-gray-400">
                    {isSubdomainMode
                      ? `© 2025 ${orgBranding.name} · Powered by Parallel-Minds`
                      : `© 2025 ${APP_NAME} · v1.0.0`}
                  </Typography>
                </Box>
              </AppCard>

              {/* Help Link */}
              <Box className="mt-6 text-center">
                <a href="mailto:HumanRelations@parallel-minds.com" className="text-sm text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors inline-flex items-center">
                    <Icon name="HelpCircle" size={16} className="mr-1" />
                  Need help signing in?
                </a>
              </Box>
            </Box>
          </Box>

          {/* Floating Animation Styles */}
          <style>{`
            @keyframes float {
              0%, 100% {
                transform: translateY(0px);
              }
              50% {
                transform: translateY(-20px);
              }
            }
            .animate-float {
              animation: float 6s ease-in-out infinite;
            }
          `}</style>
        </Box>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default LoginPage;
